import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { useToast } from '../../hooks/use-toast';
import api from '../../services/api';
import {
  Users,
  Plus,
  Search,
  Phone,
  Mail,
  Building2,
  DollarSign,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  ArrowRight,
  MessageSquare,
  RefreshCw,
  Loader2,
  User,
  Tag,
  Briefcase,
  TrendingUp,
  Target,
  Activity,
  ListTodo
} from 'lucide-react';

const STATUS_CONFIG = {
  new: { label: 'Nuevo', color: 'bg-blue-100 text-blue-700', icon: Plus },
  contacted: { label: 'Contactado', color: 'bg-yellow-100 text-yellow-700', icon: Phone },
  qualified: { label: 'Calificado', color: 'bg-purple-100 text-purple-700', icon: CheckCircle },
  proposal: { label: 'Propuesta', color: 'bg-orange-100 text-orange-700', icon: Briefcase },
  negotiation: { label: 'Negociación', color: 'bg-pink-100 text-pink-700', icon: MessageSquare },
  won: { label: 'Ganado', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  lost: { label: 'Perdido', color: 'bg-gray-100 text-gray-600', icon: XCircle }
};

const SOURCE_CONFIG = {
  website: 'Sitio Web',
  referral: 'Referido',
  event: 'Evento',
  webinar: 'Webinar',
  social_media: 'Redes Sociales',
  cold_call: 'Llamada Fría',
  other: 'Otro'
};

const AdminCRM = () => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showNewLeadModal, setShowNewLeadModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const { toast } = useToast();

  const [newLead, setNewLead] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    source: 'website',
    estimated_value: 0,
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, [searchTerm, statusFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter) params.append('status', statusFilter);

      const [leadsRes, statsRes, pipelineRes, tasksRes] = await Promise.all([
        api.get(`/crm/leads?${params.toString()}`),
        api.get('/crm/stats'),
        api.get('/crm/pipeline'),
        api.get('/crm/tasks?completed=false')
      ]);

      setLeads(leadsRes.data.leads || []);
      setStats(statsRes.data);
      setPipeline(pipelineRes.data);
      setTasks(tasksRes.data.tasks || []);
    } catch (error) {
      console.error('Error fetching CRM data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLead = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post('/crm/leads', newLead);
      toast({
        title: "Lead creado",
        description: `${newLead.name} agregado al CRM`
      });
      setShowNewLeadModal(false);
      setNewLead({
        name: '',
        email: '',
        phone: '',
        company: '',
        source: 'website',
        estimated_value: 0,
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "No se pudo crear el lead",
        variant: "destructive"
      });
    } finally {
      setCreating(false);
    }
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      await api.put(`/crm/leads/${leadId}/status?status=${newStatus}`);
      toast({
        title: "Estado actualizado",
        description: `Lead movido a ${STATUS_CONFIG[newStatus]?.label}`
      });
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo actualizar el estado",
        variant: "destructive"
      });
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="admin-crm-title">
            CRM - Gestión de Leads
          </h1>
          <p className="text-gray-500 mt-1">
            Pipeline de ventas y seguimiento de prospectos
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualizar
          </Button>
          <Dialog open={showNewLeadModal} onOpenChange={setShowNewLeadModal}>
            <DialogTrigger asChild>
              <Button className="bg-gray-900 hover:bg-gray-800" data-testid="new-lead-btn">
                <Plus className="h-4 w-4 mr-2" />
                Nuevo Lead
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Crear Nuevo Lead</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateLead} className="space-y-4 mt-4">
                <div>
                  <Label>Nombre *</Label>
                  <Input
                    value={newLead.name}
                    onChange={(e) => setNewLead({...newLead, name: e.target.value})}
                    required
                    data-testid="lead-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={newLead.email}
                      onChange={(e) => setNewLead({...newLead, email: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Teléfono</Label>
                    <Input
                      type="tel"
                      value={newLead.phone}
                      onChange={(e) => setNewLead({...newLead, phone: e.target.value})}
                    />
                  </div>
                </div>
                <div>
                  <Label>Empresa</Label>
                  <Input
                    value={newLead.company}
                    onChange={(e) => setNewLead({...newLead, company: e.target.value})}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Fuente</Label>
                    <Select
                      value={newLead.source}
                      onValueChange={(value) => setNewLead({...newLead, source: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(SOURCE_CONFIG).map(([key, label]) => (
                          <SelectItem key={key} value={key}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Valor Estimado ($)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={newLead.estimated_value}
                      onChange={(e) => setNewLead({...newLead, estimated_value: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                </div>
                <div>
                  <Label>Notas</Label>
                  <Textarea
                    value={newLead.notes}
                    onChange={(e) => setNewLead({...newLead, notes: e.target.value})}
                    rows={3}
                  />
                </div>
                <Button type="submit" className="w-full bg-gray-900 hover:bg-gray-800" disabled={creating}>
                  {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                  Crear Lead
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.total_leads || 0}</p>
                <p className="text-sm text-gray-500">Total Leads</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.conversion_rate || 0}%</p>
                <p className="text-sm text-gray-500">Conversión</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{formatCurrency(stats?.total_value || 0)}</p>
                <p className="text-sm text-gray-500">Valor Pipeline</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center">
                <ListTodo className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.overdue_tasks || 0}</p>
                <p className="text-sm text-gray-500">Tareas Vencidas</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="leads" className="space-y-4">
        <TabsList>
          <TabsTrigger value="leads">
            <Users className="h-4 w-4 mr-2" />
            Leads
          </TabsTrigger>
          <TabsTrigger value="pipeline">
            <Target className="h-4 w-4 mr-2" />
            Pipeline
          </TabsTrigger>
          <TabsTrigger value="tasks">
            <ListTodo className="h-4 w-4 mr-2" />
            Tareas ({tasks.length})
          </TabsTrigger>
        </TabsList>

        {/* Leads Tab */}
        <TabsContent value="leads">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Buscar leads..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={statusFilter || "all"} onValueChange={(val) => setStatusFilter(val === "all" ? "" : val)}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Filtrar estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {leads.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No hay leads</p>
                  <p className="text-sm mt-1">Crea tu primer lead usando el botón de arriba</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {leads.map((lead) => {
                    const statusConfig = STATUS_CONFIG[lead.status] || STATUS_CONFIG.new;
                    return (
                      <div key={lead.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                        <div className="flex items-center gap-4">
                          <div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center">
                            <User className="h-6 w-6 text-gray-500" />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{lead.name}</p>
                            <div className="flex items-center gap-3 text-sm text-gray-500">
                              {lead.company && (
                                <span className="flex items-center">
                                  <Building2 className="h-3 w-3 mr-1" />
                                  {lead.company}
                                </span>
                              )}
                              {lead.email && (
                                <span className="flex items-center">
                                  <Mail className="h-3 w-3 mr-1" />
                                  {lead.email}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {lead.estimated_value > 0 && (
                            <span className="font-medium text-green-600">
                              {formatCurrency(lead.estimated_value)}
                            </span>
                          )}
                          <Badge className={statusConfig.color}>
                            {statusConfig.label}
                          </Badge>
                          <Select
                            value={lead.status}
                            onValueChange={(value) => updateLeadStatus(lead.id, value)}
                          >
                            <SelectTrigger className="w-8 h-8 p-0">
                              <ArrowRight className="h-4 w-4" />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                                <SelectItem key={key} value={key}>{config.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pipeline Tab */}
        <TabsContent value="pipeline">
          <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {Object.entries(STATUS_CONFIG).map(([status, config]) => {
              const stageLeads = pipeline?.pipeline?.[status] || [];
              const stageValue = pipeline?.summary?.[status]?.value || 0;
              return (
                <Card key={status} className="min-h-[300px]">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <Badge className={config.color}>{config.label}</Badge>
                      <span className="text-sm font-medium">{stageLeads.length}</span>
                    </div>
                    {stageValue > 0 && (
                      <p className="text-xs text-gray-500">{formatCurrency(stageValue)}</p>
                    )}
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {stageLeads.map((lead) => (
                      <div key={lead.id} className="p-2 bg-white rounded border border-gray-200 text-sm">
                        <p className="font-medium truncate">{lead.name}</p>
                        {lead.company && (
                          <p className="text-xs text-gray-500 truncate">{lead.company}</p>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Tasks Tab */}
        <TabsContent value="tasks">
          <Card>
            <CardContent className="p-6">
              {tasks.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <ListTodo className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No hay tareas pendientes</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {tasks.map((task) => (
                    <div key={task.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium">{task.title}</p>
                        {task.description && (
                          <p className="text-sm text-gray-500">{task.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={
                          task.priority === 'high' ? 'border-red-500 text-red-500' :
                          task.priority === 'medium' ? 'border-yellow-500 text-yellow-500' :
                          'border-gray-500 text-gray-500'
                        }>
                          {task.priority}
                        </Badge>
                        {task.due_date && (
                          <span className="text-sm text-gray-500">
                            <Calendar className="h-3 w-3 inline mr-1" />
                            {new Date(task.due_date).toLocaleDateString('es-MX')}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminCRM;
